<script setup lang="ts">
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'

import { useListsStore } from '@shared/model'
import { DropZone } from '@shared/ui/other'

import { useTutorLegalStore } from '../../model/legal/store'
import TutorLegalColumn from './TutorLegalColumn.vue'
import TutorLegalRows from './TutorLegalRows.vue'
import TutorLegalGroup from './TutorLegalGroup.vue'
import TutorLegalField from './TutorLegalField.vue'
import TutorLegalSelectField from './TutorLegalSelectField.vue'
import TutorLegalUploadedFiles from './TutorLegalUploadedFiles.vue'

defineOptions({
  name: 'TutorLegalDocuments',
})

const tutorLegalStore = useTutorLegalStore()
const listsStore = useListsStore()

const dropZoneRef = ref<InstanceType<typeof DropZone> | null>(null)

const documentTypeOptions = computed<AppSelectOption[]>(() => listsStore.getTutorBillingDocumentTypeOptions)

// After a successful save (or navigation reset) pendingFiles is emptied in the
// store — mirror that into the DropZone's internal list via its exposed clear().
watch(
  () => tutorLegalStore.getPendingFiles.length,
  (length) => {
    if (length === 0) dropZoneRef.value?.clear()
  },
)
</script>

<template>
  <TutorLegalColumn>
    <TutorLegalGroup title="Documents">
      <TutorLegalRows>
        <TutorLegalSelectField
          name="document_type"
          label="Document type"
          placeholder="Select document type"
          :options="documentTypeOptions"
          :model-value="tutorLegalStore.getForm.document_type"
          :state="tutorLegalStore.getFieldState('document_type')"
          @update:model-value="value => tutorLegalStore.setFormField('document_type', value)"
        />
        <TutorLegalField
          label="Document ID"
          :model-value="tutorLegalStore.getForm.document_id"
          :state="tutorLegalStore.getFieldState('document_id')"
          @update:model-value="value => tutorLegalStore.setFormField('document_id', value)"
        />
      </TutorLegalRows>
    </TutorLegalGroup>

    <TutorLegalUploadedFiles />

    <TutorLegalGroup title="Upload document photo">
      <DropZone
        ref="dropZoneRef"
        :max-files="5"
        uploaded-title=""
        @accept="tutorLegalStore.acceptFiles"
        @remove="tutorLegalStore.removePendingFile"
      />
    </TutorLegalGroup>
  </TutorLegalColumn>
</template>
